import { useId, useMemo, useRef, useState } from 'react';
import { GroupedCombobox, type ComboLoadResult, type ComboOption } from '../../job-roles/components/guided/GroupedCombobox';
import type { ControlA11y } from '../../job-roles/guidance/GuidedField';
import { healthcareRatesGateway } from '../index';
import { useCompanyIndustry } from '../useCompanyIndustry';
import { matchesSuggestion, type Discipline } from '../types';
import '../../job-roles/job-roles.css';
import '../healthcare-rates.css';

const ADD_ACTION = 'add-discipline';

export interface DisciplineFieldProps {
  a11y: ControlA11y;
  /** The chosen discipline's id ('' for none) and its name, so the field can show it before the list has loaded. */
  value: string;
  valueLabel?: string | undefined;
  disabled?: boolean;
  /** Offer "Add a discipline" (hidden when the server says the person cannot manage disciplines). */
  canAdd: boolean;
  /** Only these disciplines are offered (for example the employee's current assignments). Omit for the whole catalog. */
  onlyIds?: readonly string[] | undefined;
  /** Disciplines to leave out (for example ones the employee already holds). */
  excludeIds?: readonly string[] | undefined;
  onSelect: (discipline: Discipline) => void;
  onClear?: (() => void) | undefined;
  /** "Add a discipline" was chosen; `typed` is what the person had typed, to start the new name from. */
  onAddRequested: (typed: string) => void;
  placeholder?: string | undefined;
}

/**
 * The Worker category picker (called Discipline in many healthcare organizations): a searchable combobox over `GET /api/disciplines`. EPN's default
 * worker categories suggested for the company's industry are listed first, then the company's own, and a checkbox shows every industry's defaults. It
 * has a clear button, an empty state, loading and error states with "Try again", and an "Add a worker category" row for a person who may manage them.
 * A worker category is the worker's professional or trade classification (Registered Nurse, Electrician), never the work performed on a visit or job.
 */
export function DisciplineField({ a11y, value, valueLabel, disabled = false, canAdd, onlyIds, excludeIds, onSelect, onClear, onAddRequested, placeholder }: DisciplineFieldProps) {
  const uid = useId();
  const { tags, label: industryText } = useCompanyIndustry();
  const [showAll, setShowAll] = useState(false);
  const narrowed = Boolean(tags.length > 0 && industryText && !showAll && !onlyIds);
  /** Every discipline the list has shown, by id, so a choice hands back the whole record without asking the server again. */
  const seen = useRef(new Map<string, Discipline>());

  const load = useMemo(
    () => async (query: string, signal: AbortSignal): Promise<ComboLoadResult> => {
      const all = await healthcareRatesGateway.listDisciplines(query ? { search: query } : undefined, signal);
      const allowed = all.filter((d) => (!onlyIds || onlyIds.includes(d.id)) && !excludeIds?.includes(d.id));
      for (const d of allowed) seen.current.set(d.id, d);
      const toOption = (d: Discipline): ComboOption => ({ value: d.id, label: d.name, meta: d.code, detail: d.description || null, custom: !d.isSystem });
      const system = allowed.filter((d) => d.isSystem);
      const company = allowed.filter((d) => !d.isSystem);
      const groups: ComboLoadResult['groups'] = [];
      if (narrowed) {
        groups.push({ key: 'suggested', label: `Suggested for ${industryText}`, options: system.filter((d) => matchesSuggestion(d.industries, tags)).map(toOption) });
      } else if (system.length > 0) {
        groups.push({ key: 'system', label: 'EPN default worker categories', options: system.map(toOption) });
      }
      groups.push({ key: 'company', label: 'Your company’s worker categories', options: company.map(toOption) });
      const found = groups.some((g) => g.options.length > 0);
      if (canAdd) groups.push({ key: 'actions', label: 'Not in the list?', options: [{ value: ADD_ACTION, label: '+ Add a worker category', actionId: ADD_ACTION }] });
      return {
        groups,
        notice: !found
          ? (query ? `No worker category matches “${query}”.${canAdd ? ' You can add it as a new worker category.' : ''}` : 'There are no worker categories to choose from yet.')
          : narrowed ? `Showing the defaults suggested for ${industryText}. Tick “Show worker categories for every industry” to see them all.` : null,
      };
    },
    [canAdd, excludeIds, tags, industryText, narrowed, onlyIds],
  );

  return (
    <>
      <GroupedCombobox
        a11y={a11y}
        value={value}
        valueLabel={valueLabel}
        load={load}
        disabled={disabled}
        placeholder={placeholder ?? 'Search worker categories'}
        clearLabel="worker category"
        emptyText="No worker categories match. Try a different word."
        {...(onClear ? { onClear } : {})}
        onSelect={(o) => {
          const chosen = seen.current.get(o.value);
          if (chosen) onSelect(chosen);
        }}
        onAction={(action, typed) => { if (action === ADD_ACTION) onAddRequested(typed); }}
      />
      {tags.length > 0 && industryText && !onlyIds && (
        <label className="hdc-showall" htmlFor={`${uid}-all`}>
          <input id={`${uid}-all`} type="checkbox" checked={showAll} disabled={disabled} onChange={(e) => setShowAll(e.target.checked)} />
          <span>Show worker categories for every industry</span>
        </label>
      )}
    </>
  );
}
